import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import { ReactComponent as SoulMatrixChart } from '../../../assets/charts/soul-matrix.svg';
import { IHealthMap, IMatrix, IPurposeMap } from '../../../api/charts/soulMatrix';
import { HealthMap } from '../../HealthMap';
import { PurposeMap } from '../../PurposeMap/PurposeMap';
import { Tab, TabList, Tabs } from '../../Tabs';

import styles from './SoulMatrix.module.scss';

const createElementDataMap = (matrix: IMatrix) => ({
  visit_card_channel_left: matrix.visit_card_channel.left,
  visit_card_channel_center: matrix.visit_card_channel.center,
  visit_card_channel_right: matrix.visit_card_channel.right,

  talents_top: matrix.talents.top,
  talents_center: matrix.talents.center,
  talents_bottom: matrix.talents.bottom,

  main_material_channel_left: matrix.main_material_channel.left,
  main_material_channel_center: matrix.main_material_channel.center,
  main_material_channel_right: matrix.main_material_channel.right,

  soul_male_channel_top_left_top: matrix.soul_male_channel.top_left?.top,
  soul_male_channel_top_left_center: matrix.soul_male_channel.top_left?.center,
  soul_male_channel_top_left_bottom: matrix.soul_male_channel.top_left?.bottom,
  soul_male_channel_material_male_channel_top: matrix.soul_male_channel.material_male_channel?.top,
  soul_male_channel_material_male_channel_center: matrix.soul_male_channel.material_male_channel?.center,
  soul_male_channel_material_male_channel_bottom: matrix.soul_male_channel.material_male_channel?.bottom,

  soul_female_channel_top_right_top: matrix.soul_female_channel.top_right?.top,
  soul_female_channel_top_left_center: matrix.soul_female_channel.top_right?.center,
  soul_female_channel_top_left_bottom: matrix.soul_female_channel.top_right?.bottom,
  soul_female_channel_material_female_channel_top: matrix.soul_female_channel.material_female_channel?.top,
  soul_female_channel_material_female_channel_center: matrix.soul_female_channel.material_female_channel?.center,
  soul_female_channel_material_female_channel_bottom: matrix.soul_female_channel.material_female_channel?.bottom,

  karmic_tail_top: matrix.karmic_tail?.top,
  karmic_tail_center: matrix.karmic_tail?.center,
  karmic_tail_bottom: matrix.karmic_tail?.bottom,

  main_arcane: matrix.main_arcane,
  comfort_zone_channel_center: matrix.comfort_zone_channel?.center,
  comfort_zone_channel_right: matrix.comfort_zone_channel?.right,
  health_top_arcane: matrix.health_top_arcane,
  health_left_arcane: matrix.health_left_arcane,

  finance_center: matrix.finance_center,
  intersection_of_channels_of_relations_and_finance: matrix.intersection_of_channels_of_relations_and_finance,
  relationship_center: matrix.relationship_center,
});

/**
 * Set dynamic text values and fade them in.
 * On first call the numbers start invisible and transition to visible;
 * on subsequent calls the text updates instantly (no repeated animation).
 */
function updateElementText(
  ref: React.MutableRefObject<SVGSVGElement | null>,
  dataMap: Record<string, number>,
  animate: boolean,
) {
  Object.entries(dataMap).forEach(([elementId, elementValue]) => {
    const element = ref.current?.querySelector(`text#${elementId}`) as SVGTextElement | null;
    if (!element) return;

    if (animate) {
      element.style.opacity = '0';
      element.style.transition = 'opacity 0.5s ease';
    }

    element.textContent = elementValue?.toString() || '';

    if (animate) {
      requestAnimationFrame(() => {
        element.style.opacity = '1';
      });
    }
  });
}

function updateDynamicYearArcane(
  svgRef: React.MutableRefObject<SVGSVGElement | null>,
  yearForecast: Record<string, number>,
  animate: boolean,
) {
  const dynamicYearGroup = svgRef.current?.querySelector('#dynamic_year_arcane');
  if (!dynamicYearGroup) return;

  const elements = dynamicYearGroup.querySelectorAll<SVGTextElement>('text[data-key]');
  elements.forEach(element => {
    const key = element.getAttribute('data-key');
    if (!key || !yearForecast[key]) return;

    if (animate) {
      element.style.opacity = '0';
      element.style.transition = 'opacity 0.5s ease';
    }

    element.textContent = yearForecast[key].toString();

    if (animate) {
      requestAnimationFrame(() => {
        element.style.opacity = '1';
      });
    }
  });
}

function updateGroupVisibility(svgRef: React.MutableRefObject<SVGSVGElement | null>, positions: number[]) {
  const svg = svgRef.current;
  if (!svg) return;

  const allGroups = svg.querySelectorAll<SVGGElement>('g.group');

  if (positions.length === 0) {
    allGroups.forEach(group => {
      group.classList.remove('dimmed', 'highlighted');
    });
    return;
  }

  allGroups.forEach(group => {
    const id = group.getAttribute('id');
    const numericId = Number(id);

    if (positions.includes(numericId)) {
      group.classList.remove('dimmed');
      group.classList.add('highlighted');
    } else {
      group.classList.add('dimmed');
      group.classList.remove('highlighted');
    }
  });
}

interface SoulMatrixProps {
  matrix: IMatrix;
  healthMap: IHealthMap;
  purposeMap: IPurposeMap;
  positions: number[];
}

export function SoulMatrix({ matrix, healthMap, purposeMap, positions }: SoulMatrixProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const isFirstRender = useRef(true);
  const [activeDetailsTab, setActiveDetailsTab] = useState<string>('purpose');

  const updateSvgContent = useCallback(() => {
    const dataMap = createElementDataMap(matrix);
    const animate = isFirstRender.current;

    updateElementText(svgRef, dataMap, animate);
    updateDynamicYearArcane(svgRef, matrix.years_forecast, animate);

    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, [matrix]);

  useEffect(() => {
    if (svgRef.current) {
      updateSvgContent();
      updateGroupVisibility(svgRef, positions);
    }
  }, [matrix, updateSvgContent, positions]);

  return (
    <>
      <div className={styles.view}>
        <div className={styles.title}>Матрица судьбы</div>
        <SoulMatrixChart ref={svgRef} className={styles.soulMatrix} />
      </div>
      <Tabs className={styles.details} activeTab={activeDetailsTab} onTabChange={tab => setActiveDetailsTab(tab)}>
        <TabList className={styles.tabList}>
          <Tab className={styles.tab} tabId="purpose">
            Предназначение
          </Tab>
          <Tab className={styles.tab} tabId="health">
            Карта здоровья
          </Tab>
        </TabList>
      </Tabs>
      {/* @ts-expect-error framer-motion types incompatible with @types/react 18 */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeDetailsTab}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          {activeDetailsTab === 'purpose' ? (
            <PurposeMap highlightedIDs={positions} data={purposeMap} />
          ) : (
            <HealthMap highlightedIDs={positions} data={healthMap} />
          )}
        </motion.div>
      </AnimatePresence>
    </>
  );
}
